import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import api from '../../utils/api';
import Navigation from '../Navigation/navigation';

const EditSkills = () => {
  const { staffId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [staffData, setStaffData] = useState(null);
  const [languageSkills, setLanguageSkills] = useState([]);
  const [computerSkills, setComputerSkills] = useState([]);
  const [activeTab, setActiveTab] = useState('language');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [referenceData, setReferenceData] = useState({
    languages: [],
    communicationMethods: [],
    capacityLevels: [],
    computerSkills: []
  });

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    if (staffId) {
      fetchStaffData();
      fetchSkillsData();
      fetchReferenceData();
    }
  }, [staffId, isAuthenticated, user, navigate]);

  const fetchStaffData = async () => {
    try {
      const response = await api.get(`/api/staff/${staffId}`);
      setStaffData(response.data);
    } catch (error) {
      console.error('Error fetching staff data:', error);
      setError('Error retrieving staff information.');
    }
  };

  const fetchSkillsData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/staff/${staffId}/skills`);
      const skillsData = response.data || {};
      
      // The backend now returns { languageSkills: [], computerSkills: [] }
      setLanguageSkills(skillsData.languageSkills || []);
      setComputerSkills(skillsData.computerSkills || []);
      
      // Debug: Log each computer skill to see the other_computer_skills field
      if (skillsData.computerSkills) {
        skillsData.computerSkills.forEach((skill, index) => {
        });
      }
    } catch (error) {
      console.error('Error fetching skills data:', error);
      setError('Error retrieving skills information.');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [languages, communicationMethods, capacityLevels, computerSkills] = await Promise.all([
        api.get('/api/reference/languages'),
        api.get('/api/reference/communication-methods'),
        api.get('/api/reference/capacity-levels'),
        api.get('/api/reference/computer-skills')
      ]);
      
      // Helper function to extract data from response (handles both direct arrays and wrapped responses)
      const extractData = (response) => {
        if (Array.isArray(response.data)) {
          return response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          return response.data.data;
        } else if (response.data && response.data.success && Array.isArray(response.data.data)) {
          return response.data.data;
        }
        return [];
      };
      
      setReferenceData({
        languages: extractData(languages),
        communicationMethods: extractData(communicationMethods),
        capacityLevels: extractData(capacityLevels),
        computerSkills: extractData(computerSkills)
      });
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const addLanguageSkill = () => {
    const newSkill = {
      language_id: '',
      other_language: '',
      communicate_ids: [],
      capacity_level_id: ''
    };
    setLanguageSkills(prev => [...prev, newSkill]);
  };

  const updateLanguageSkill = (index, field, value) => {
    setLanguageSkills(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeLanguageSkill = (index) => {
    setLanguageSkills(prev => prev.filter((_, i) => i !== index));
  };

  const addComputerSkill = () => {
    const newSkill = {
      computerskills_id: '',
      other_computer_skills: '',
      capacity_level_id: ''
    };
    setComputerSkills(prev => [...prev, newSkill]);
  };

  const updateComputerSkill = (index, field, value) => {
    setComputerSkills(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeComputerSkill = (index) => {
    setComputerSkills(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Send language and computer skills as separate arrays
      await api.put(`/api/staff/${staffId}/skills`, { 
        languageSkills: languageSkills,
        computerSkills: computerSkills 
      });
      
      setSuccessMessage('Skills information updated successfully!');
      setTimeout(() => {
        navigate(`/edit-staff/${staffId}`);
      }, 2000);
    } catch (error) {
      console.error('Error updating skills:', error);
      setError(error.response?.data?.message || 'Error updating skills information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/edit-staff/${staffId}`);
  };

  if (loading && !staffData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-6xl mx-auto p-5 pt-20">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h4 className="text-xl font-semibold text-gray-800">Loading Skills Information...</h4>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-6xl mx-auto p-5 pt-20">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Edit Skills Information</h1>
              <p className="text-gray-600">
                Managing skills for: <strong>{staffData?.fullname}</strong> (ID: {staffData?.staff_id})
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Success and Error Messages */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <h2 className="text-xl font-semibold">Skills Information</h2>
            <p className="text-blue-100 mt-1">Manage language and computer skills</p>
          </div>

          <div className="p-6">
            {/* Tab Navigation */}
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6">
              <button
                type="button"
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'language' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setActiveTab('language')}
              >
                Language Skills
              </button>
              <button
                type="button"
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'computer' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setActiveTab('computer')}
              >
                Computer Skills
              </button>
            </div>

            {/* Language Skills Tab */}
            {activeTab === 'language' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-800">Language Skills</h3>
                  <button 
                    type="button" 
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" 
                    onClick={addLanguageSkill}
                  >
                    Add Language Skill
                  </button>
                </div>
                
                {languageSkills.length === 0 && (
                  <div className="text-center py-8 text-gray-600">
                    <div className="text-4xl mb-4">🗣️</div>
                    <p className="text-lg font-medium">No language skills added yet</p>
                    <p className="text-sm text-gray-600 mt-2">Click "Add Language Skill" to get started</p>
                  </div>
                )}
                
                {/* Group language skills by language_id since database stores separate records for each communication method */}
                {(() => {
                  // Group language skills by language_id
                  const groupedSkills = languageSkills.reduce((acc, skill) => {
                    const key = skill.language_id;
                    if (!acc[key]) {
                      acc[key] = {
                        language_id: skill.language_id,
                        language_name: skill.language_name,
                        other_language: skill.other_language,
                        capacity_level_id: skill.capacity_level_id,
                        capacity_level: skill.capacity_level,
                        communicate_ids: [],
                        records: []
                      };
                    }
                    acc[key].communicate_ids.push(skill.communicate_id);
                    acc[key].records.push(skill);
                    return acc;
                  }, {});

                  const groupedSkillsArray = Object.values(groupedSkills);
                  
                  return groupedSkillsArray.map((groupedSkill, groupIndex) => (
                    <div key={groupedSkill.language_id} className="border border-gray-200 rounded-lg p-6">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-md font-semibold text-gray-800">
                          Language Skill: {groupedSkill.language_name} 
                          {groupedSkill.other_language && ` (${groupedSkill.other_language})`}
                        </h4>
                        <button
                          type="button"
                          className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm transition-colors"
                          onClick={() => {
                            // Remove all records for this language
                            const newSkills = languageSkills.filter(skill => skill.language_id !== groupedSkill.language_id);
                            setLanguageSkills(newSkills);
                          }}
                        >
                          Remove Language
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Language</label>
                          <select
                            value={groupedSkill.language_id}
                            onChange={(e) => {
                              // Update all records for this language
                              const newSkills = languageSkills.map(skill => 
                                skill.language_id === groupedSkill.language_id 
                                  ? { ...skill, language_id: e.target.value }
                                  : skill
                              );
                              setLanguageSkills(newSkills);
                            }}
                            className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                          >
                            <option value="">Select Language</option>
                            {referenceData.languages.map((language) => (
                              <option key={language.id} value={language.id}>
                                {language.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {groupedSkill.language_id === '5' && (
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Other Language</label>
                            <input
                              type="text"
                              value={groupedSkill.other_language || ''}
                              onChange={(e) => {
                                const newSkills = languageSkills.map(skill => 
                                  skill.language_id === groupedSkill.language_id 
                                    ? { ...skill, other_language: e.target.value }
                                    : skill
                                );
                                setLanguageSkills(newSkills);
                              }}
                              className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                              placeholder="Specify other language"
                            />
                          </div>
                        )}

                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Communication Methods</label>
                          <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                            {referenceData.communicationMethods.map((method) => {
                              const isChecked = groupedSkill.communicate_ids.includes(method.id);
                              
                              return (
                                <label key={method.id} className="flex items-center space-x-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        // Add a new record for this communication method
                                        const baseRecord = groupedSkill.records[0];
                                        const newRecord = {
                                          ...baseRecord,
                                          id: null, // Will be assigned by backend
                                          communicate_id: method.id
                                        };
                                        setLanguageSkills([...languageSkills, newRecord]);
                                      } else {
                                        // Remove records with this communication method for this language
                                        const newSkills = languageSkills.filter(skill => 
                                          !(skill.language_id === groupedSkill.language_id && skill.communicate_id === method.id)
                                        );
                                        setLanguageSkills(newSkills);
                                      }
                                    }}
                                    className="rounded border-gray-300 text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                  />
                                  <span className={`${isChecked ? 'font-semibold text-blue-700' : ''}`}>
                                    {method.communicate}
                                    {isChecked && (
                                      <span className="ml-1 text-green-600">✓</span>
                                    )}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">Capacity Level</label>
                          <select
                            value={groupedSkill.capacity_level_id}
                            onChange={(e) => {
                              // Update all records for this language
                              const newSkills = languageSkills.map(skill => 
                                skill.language_id === groupedSkill.language_id 
                                  ? { ...skill, capacity_level_id: e.target.value }
                                  : skill
                              );
                              setLanguageSkills(newSkills);
                            }}
                            className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                          >
                            <option value="">Select Level</option>
                            {referenceData.capacityLevels.map((level) => (
                              <option key={level.id} value={level.id}>
                                {level.level}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}

            {/* Computer Skills Tab */}
            {activeTab === 'computer' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-800">Computer Skills</h3>
                  <button 
                    type="button" 
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" 
                    onClick={addComputerSkill}
                  >
                    Add Computer Skill
                  </button>
                </div>
                
                {computerSkills.length === 0 && (
                  <div className="text-center py-8 text-gray-600">
                    <div className="text-4xl mb-4">💻</div>
                    <p className="text-lg font-medium">No computer skills added yet</p>
                    <p className="text-sm text-gray-600 mt-2">Click "Add Computer Skill" to get started</p>
                  </div>
                )}
                
                {computerSkills.map((skill, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-md font-semibold text-gray-800">Computer Skill #{index + 1}</h4>
                      <button
                        type="button"
                        className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm transition-colors"
                        onClick={() => removeComputerSkill(index)}
                      >
                        Remove
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Computer Skill</label>
                        <select
                          value={skill.computerskills_id}
                          onChange={(e) => updateComputerSkill(index, 'computerskills_id', e.target.value)}
                          className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                        >
                          <option value="">Select Skill</option>
                          {referenceData.computerSkills.map((compSkill) => (
                            <option key={compSkill.id} value={compSkill.id}>
                              {compSkill.skills}
                            </option>
                          ))}
                        </select>
                      </div>

                      {(skill.computerskills_id === '999' || (skill.other_computer_skills && skill.other_computer_skills.trim() !== '')) && (
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Other Computer Skill
                            {skill.other_computer_skills && skill.other_computer_skills.trim() !== '' && (
                              <span className="text-blue-600 ml-1">(has saved data)</span>
                            )}
                          </label>
                          <input
                            type="text"
                            value={skill.other_computer_skills || ''}
                            onChange={(e) => updateComputerSkill(index, 'other_computer_skills', e.target.value)}
                            className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                            placeholder="Enter other computer skill"
                          />
                        </div>
                      )}

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Capacity Level</label>
                        <select
                          value={skill.capacity_level_id}
                          onChange={(e) => updateComputerSkill(index, 'capacity_level_id', e.target.value)}
                          className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                        >
                          <option value="">Select Level</option>
                          {referenceData.capacityLevels.map((level) => (
                            <option key={level.id} value={level.id}>
                              {level.level}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSkills;
